import type { AdminEconomicsInput, AdminEconomicsState } from './api';
import { ApiError, apiRequest, fetchMe, login, logout } from './api-runtime';
export { ApiError, apiRequest, fetchMe, login, logout };
export type * from './api';

const empty = <T>(value: T): Promise<T> => Promise.resolve(value);
const unsupported = (name: string): never => {
  throw new ApiError(`${name} is not available in the Studio admin`, 403);
};

function usageQuery(params?: Record<string, unknown>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  }
  const result = query.toString();
  return result ? `?${result}` : '';
}

export const adminApi = {
  studioDashboard: () => apiRequest('/admin/v2/studio/dashboard'),
  appDashboard: () => apiRequest('/admin/v2/studio/dashboard'),
  platformDashboard: () => apiRequest('/admin/v2/studio/dashboard'),
  studioUsers: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/users${usageQuery(params)}`),
  appUsers: () => empty({ items: [], next_cursor: null }),
  users: () => empty({ items: [], next_cursor: null }),
  studioUser: (id: string) => apiRequest(`/admin/v2/studio/users/${id}`),
  appUser: (id: string) => apiRequest(`/admin/v2/studio/users/${id}`),
  platformUser: (id: string) => apiRequest(`/admin/v2/studio/users/${id}`),
  studioPlans: () => apiRequest('/admin/v2/studio/plans'),
  appPlans: () => apiRequest('/admin/v2/studio/plans'),
  getEconomics: (product: 'APP' | 'STUDIO' = 'STUDIO') =>
    product === 'STUDIO'
      ? apiRequest<AdminEconomicsState>('/admin/v2/economics?product=STUDIO')
      : unsupported('APP economics'),
  createEconomicsDraft: (body: AdminEconomicsInput) =>
    apiRequest<{ id: string; version: number }>('/admin/v2/economics/draft', {
      method: 'POST',
      body,
    }),
  publishEconomicsDraft: () =>
    apiRequest<{ id: string; version: number }>('/admin/v2/economics/publish', { method: 'POST' }),
  rollbackEconomics: (version: number) =>
    apiRequest<{ id: string; version: number }>('/admin/v2/economics/rollback', {
      method: 'POST',
      body: { version },
    }),
  studioWorkspaces: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/workspaces${usageQuery(params)}`),
  studioUsageOverview: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/usage/overview${usageQuery(params)}`),
  studioUsageByModel: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/usage/by-model${usageQuery(params)}`),
  studioUsageTimeseries: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/usage/timeseries${usageQuery(params)}`),
  studioUsageLedger: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/usage/ledger${usageQuery(params)}`),
  appUsageOverview: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/usage/overview${usageQuery(params)}`),
  appUsageByModel: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/usage/by-model${usageQuery(params)}`),
  appUsageTimeseries: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/usage/timeseries${usageQuery(params)}`),
  appUsageLedger: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/studio/usage/ledger${usageQuery(params)}`),
  models: (params?: Record<string, unknown>) => apiRequest(`/admin/v2/models${usageQuery({ ...params, product: 'studio' })}`),
  updateModel: (id: string, payload: unknown) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}`, { method: 'PATCH', body: payload }),
  getModelStudioConfigV2: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/v2`),
  saveModelStudioConfigDraft: (id: string, payload: unknown) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/draft`, { method: 'PATCH', body: payload }),
  validateModelStudioConfigV2: (id: string, payload?: unknown) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/validate`, { method: 'POST', body: payload ?? {} }),
  importModelStudioCapabilities: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/import-capabilities`, { method: 'POST' }),
  publishModelStudioConfigV2: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/publish`, { method: 'POST' }),
  rollbackModelStudioConfigV2: (id: string, version: number) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/rollback`, { method: 'POST', body: { version } }),
  previewModelStudioConfigV2Matrix: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/preview-matrix`),
  getModelStudioPricing: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-pricing`),
  syncModelStudioPricing: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-pricing/sync`, { method: 'POST' }),
  createModelStudioSellPriceDraft: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-pricing/sell-draft`, { method: 'POST' }),
  publishModelStudioSellPriceDraft: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-pricing/sell-publish`, { method: 'POST' }),
  rollbackModelStudioSellPrice: (id: string, version: number) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-pricing/sell-rollback`, { method: 'POST', body: { version } }),
  syncModelCapabilities: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/sync-capabilities`, { method: 'POST' }),
  saveModelManualCapabilities: (id: string, payload: unknown) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/manual-capabilities`, { method: 'POST', body: payload }),
  getModelCapabilityHistory: (id: string) => apiRequest(`/admin/v2/models/${encodeURIComponent(id)}/studio-config/capability-history`),
  providers: () => empty({ items: [] }),
  updateProvider: () => unsupported('Provider settings'),
  resyncModels: () => unsupported('Provider resync'),
  payments: () => empty({ items: [], next_cursor: null }),
  agents: () => empty({ items: [], next_cursor: null }),
  agent: () => unsupported('APP agents'),
  userCreditLedger: () => unsupported('APP credit ledger'),
  grantCredits: () => unsupported('APP credits'),
  grantAppCredits: () => unsupported('APP credits'),
  assignAppPlan: () => unsupported('APP plans'),
  assignPlan: () => unsupported('APP plans'),
  patchUser: () => unsupported('Global identity mutation'),
  revokeUserSessions: () => unsupported('Global session mutation'),
  deleteUser: () => unsupported('User deletion'),
  impersonate: () => unsupported('User impersonation'),
};
